import { derived, writable, get } from 'svelte/store';
import formatMessage from 'format-message';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

var buildKey = function (base, curr) { return base ? base + "." + curr : curr; };
var flatObject = function (object) {
    var result = {};
    var iterate = function (item, currentKey) {
        Object.keys(item).forEach(function (key) {
            var value = item[key];
            if (typeof value === 'object')
                iterate(value, buildKey(currentKey, key));
            else
                result[buildKey(currentKey, key)] = value;
        });
    };
    iterate(object, '');
    return result;
};

var flatTranslation = function (translations) { return Object.keys(translations).reduce(function (acc, key) {
    var _a;
    return (__assign(__assign({}, acc), (_a = {}, _a[key] = flatObject(translations[key]), _a)));
}, {}); };
var merge = function (obj1, obj2) {
    if (obj1 === void 0) { obj1 = {}; }
    return Object.keys(obj2).reduce(function (acc, key) {
        if (obj2[key] !== null && typeof obj2[key] === 'object') {
            acc[key] = merge(acc[key], obj2[key]);
        }
        else {
            acc[key] = obj2[key];
        }
        return acc;
    }, obj1);
};
function createMergeableStore(defaultValue) {
    var _a = writable(defaultValue), subscribe = _a.subscribe, set = _a.set, update = _a.update;
    var updateMerging = function (newTranslations) { return update(function ($translations) {
        return merge($translations, newTranslations);
    }); };
    return {
        set: set,
        subscribe: subscribe,
        update: updateMerging,
    };
}
var defaultOptions = {
    missingTranslation: 'ignore',
};
var options = createMergeableStore(defaultOptions);
var translations = createMergeableStore({});
var locales = derived(translations, function ($translations) { return Object.keys($translations); });
var createLocale = function () {
    var _a = writable(''), subscribe = _a.subscribe, set = _a.set, update = _a.update;
    var setLocale = function (newLocale) {
        if (newLocale !== '' && !get(translations)[newLocale]) {
            console.error("[svelte-intl] Couldn't find the \"" + newLocale + "\" locale.");
            return false;
        }
        set(newLocale);
        return true;
    };
    return {
        subscribe: subscribe,
        update: update,
        set: setLocale,
    };
};
var locale = createLocale();
var translate = derived([locale, translations, options], function (stores) {
    var $locale = stores[0], $translations = stores[1], $options = stores[2];
    formatMessage.setup(__assign(__assign({}, $options), { locale: $locale, translations: flatTranslation($translations) }));
    return formatMessage;
});
var _ = translate;
/** For tests */
var clear = function () {
    locale.set('');
    translations.set({});
    options.set(defaultOptions);
};

var getBrowserLocale = function (defaultLocale) {
    if (defaultLocale === void 0) { defaultLocale = 'en'; }
    var targets = typeof window === 'undefined'
        ? [defaultLocale] // there is no window (sapper | node/webpack)
        : window.navigator.languages || // user language preferences list
            [
                window.navigator.userLanguage || // IE 10-
                    window.navigator.language,
            ];
    var currentLocales = get(locales);
    var _loop_1 = function (i) {
        if (currentLocales.includes(targets[i]))
            return { value: targets[i] }; // exact match
        var bestMatch = currentLocales.find(function (locale) { return targets[i].startsWith(locale); });
        if (bestMatch)
            return { value: bestMatch }; // en-US -> en
    };
    for (var i = 0; i < targets.length; i = i + 1) {
        var state_1 = _loop_1(i);
        if (typeof state_1 === "object")
            return state_1.value;
    }
    var currentLocale = get(locale);
    return currentLocale || currentLocales[0]; // default to current or just first
};

export { _, clear, defaultOptions, getBrowserLocale, locale, locales, options, translate, translations };
